import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { adminPartnerFileUrl, errorText, formatDateTime } from "@/lib/partnerApi";
import { PILL, notifyPartners } from "@/components/admin/partners/PartnerUpdatesAdmin";

const FILTERS = [["open", "Abertas"], ["resolved", "Resolvidas"], ["all", "Todas"]];

export function PartnerThreadsAdmin() {
    const [status, setStatus] = useState("open");
    const [items, setItems] = useState(null);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        api.get("/admin/partner-threads", { params: { status: status === "all" ? "" : status } })
            .then(({ data }) => setItems([...data].sort((a, b) => (b.urgent && b.status === "open") - (a.urgent && a.status === "open") || b.unread - a.unread)))
            .catch(() => setItems([]));
    }, [status]);
    useEffect(() => { setItems(null); load(); }, [load]);

    return (
        <div data-testid="admin-partner-threads">
            <div className="flex gap-2 mb-4">
                {FILTERS.map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setStatus(key)} data-testid={`partner-threads-filter-${key}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold ${status === key ? "bg-[hsl(var(--forest))] text-white" : "bg-white border border-stone-200 text-stone-600"}`}>
                        {label}
                    </button>
                ))}
            </div>
            {items === null ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : items.length === 0 ? (
                <p className="text-sm text-stone-400 italic" data-testid="partner-threads-empty">Sem questões.</p>
            ) : (
                <ul className="space-y-2">
                    {items.map((t) => (
                        <li key={t.id}>
                            <button type="button" onClick={() => setOpenId(t.id)} className={`w-full text-left bg-white rounded-lg border p-4 hover:border-[hsl(var(--copper))] transition-colors ${t.urgent && t.status === "open" ? "border-red-300" : "border-stone-200"}`} data-testid={`partner-thread-row-${t.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-semibold text-stone-900 truncate">{t.title}</div>
                                        <div className="text-xs text-stone-500 truncate">{t.partner_name} · {t.obra_title} · {t.lead_name}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        {t.urgent && <span className={`${PILL} bg-red-600 text-white`}>Urgente</span>}
                                        {t.unread && <span className={`${PILL} bg-[hsl(var(--copper))] text-white`} data-testid={`partner-thread-unread-${t.id}`}>Nova mensagem</span>}
                                        <span className={`${PILL} ${t.status === "open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{t.status === "open" ? "Aberta" : "Resolvida"}</span>
                                    </div>
                                </div>
                                <p className="text-sm text-stone-600 mt-1 line-clamp-1">{t.last_author === "team" ? "Equipa: " : ""}{t.last_preview}</p>
                                <div className="text-[11px] text-stone-400 mt-1">{formatDateTime(t.last_message_at)}</div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <ThreadDialog id={openId} onClose={() => setOpenId(null)} onChanged={() => { load(); notifyPartners(); }} />
        </div>
    );
}

function ThreadDialog({ id, onClose, onChanged }) {
    const [t, setT] = useState(null);
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        setT(null);
        setBody("");
        if (!id) return;
        api.get(`/admin/partner-threads/${id}`).then(({ data }) => {
            setT(data);
            if (data.unread) api.post(`/admin/partner-threads/${id}/read`).then(onChanged).catch(() => {});
        }).catch((e) => toast.error(errorText(e)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const act = async (kind) => {
        setBusy(kind);
        try {
            const { data } = kind === "send"
                ? await api.post(`/admin/partner-threads/${id}/messages`, { body })
                : await api.post(`/admin/partner-threads/${id}/resolve`);
            setT(data);
            if (kind === "send") setBody("");
            toast.success(kind === "send" ? "Resposta enviada ao parceiro." : "Questão marcada como resolvida.");
            onChanged();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <Dialog open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="partner-thread-dialog">
                <DialogHeader><DialogTitle className="font-display text-xl text-[hsl(var(--forest))] pr-6">{t?.title || "Questão"}</DialogTitle></DialogHeader>
                {!t ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : (
                    <div className="space-y-4">
                        <div className="text-xs text-stone-500">{t.partner_name} · {t.obra_title} · Cliente: {t.lead_name}{t.urgent && <span className={`${PILL} bg-red-600 text-white ml-2`}>Urgente</span>}</div>
                        <ol className="space-y-2.5" data-testid="partner-thread-dialog-messages">
                            {t.messages.map((m) => {
                                const team = m.author === "team";
                                return (
                                    <li key={m.id} className={`flex ${team ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[85%] rounded-xl px-3 py-2 ${team ? "bg-emerald-50 border border-emerald-100" : "bg-stone-100"}`}>
                                            <div className={`text-[10px] uppercase tracking-wider font-semibold ${team ? "text-emerald-800" : "text-[hsl(var(--copper))]"}`}>{team ? "Equipa" : m.author_name || "Parceiro"}</div>
                                            <p className="text-sm text-stone-800 whitespace-pre-wrap break-words mt-0.5">{m.body}</p>
                                            {(m.photo_file_ids || []).length > 0 && (
                                                <div className="mt-2 grid grid-cols-4 gap-1.5">
                                                    {m.photo_file_ids.map((pid) => (
                                                        <a key={pid} href={adminPartnerFileUrl(pid)} target="_blank" rel="noreferrer"><img src={adminPartnerFileUrl(pid)} alt="" className="aspect-square w-full object-cover rounded" loading="lazy" /></a>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="text-[10px] text-stone-500 mt-1">{formatDateTime(m.created_at)}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                        <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Responder ao parceiro" className="w-full px-3 py-2 border border-stone-300 rounded text-sm" data-testid="partner-thread-dialog-reply" />
                        <p className="text-[11px] text-stone-500">O parceiro recebe um aviso na plataforma e um email (sem o texto). Decisões sobre trabalhos extra seguem o processo formal de aprovação.</p>
                        <div className="flex gap-2 flex-wrap">
                            <button type="button" onClick={() => act("send")} disabled={!body.trim() || !!busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[hsl(var(--forest))] text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40" data-testid="partner-thread-dialog-send">
                                {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar resposta
                            </button>
                            {t.status === "open" && (
                                <button type="button" onClick={() => act("resolve")} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-emerald-300 text-emerald-800 bg-emerald-50 text-xs font-semibold uppercase tracking-wider" data-testid="partner-thread-dialog-resolve">
                                    {busy === "resolve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Marcar como resolvida
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
